/**
 * TextInput — Chat input bar with send button.
 *
 * 48px height, send on Enter (Shift+Enter for newline).
 */
import { createElement, useState, useCallback, useRef, type KeyboardEvent, type ChangeEvent } from 'react';

interface TextInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function TextInput({ onSend, disabled, placeholder = 'Ask Charlotte...' }: TextInputProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue('');
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = '48px';
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    // Auto-resize up to 3 lines
    const el = e.target;
    el.style.height = '48px';
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, []);

  return createElement(
    'div',
    {
      style: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '8px',
        padding: '8px 0 0',
        borderTop: '1px solid var(--sw-border)',
      },
    },
    // Textarea
    createElement('textarea', {
      ref: inputRef,
      value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      placeholder,
      disabled,
      rows: 1,
      'aria-label': placeholder,
      style: {
        flex: 1,
        minHeight: '48px',
        maxHeight: '96px',
        padding: '12px',
        border: '1px solid var(--sw-border)',
        borderRadius: 'var(--sw-border-radius)',
        backgroundColor: 'var(--sw-bg-surface)',
        color: 'var(--sw-text-primary)',
        fontFamily: 'var(--sw-font-family)',
        fontSize: '14px',
        lineHeight: 1.5,
        resize: 'none' as const,
        outline: 'none',
        boxSizing: 'border-box' as const,
        opacity: disabled ? 0.5 : 1,
      },
    }),
    // Send button
    createElement(
      'button',
      {
        type: 'button',
        onClick: handleSend,
        disabled: disabled || !value.trim(),
        'aria-label': 'Send message',
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          minWidth: '48px',
          border: 'none',
          borderRadius: 'var(--sw-border-radius)',
          backgroundColor:
            disabled || !value.trim()
              ? 'var(--sw-bg-surface)'
              : 'var(--sw-accent-primary)',
          color: disabled || !value.trim() ? 'var(--sw-text-secondary)' : '#000',
          cursor: disabled || !value.trim() ? 'default' : 'pointer',
          padding: 0,
          flexShrink: 0,
        },
      },
      createElement(
        'svg',
        {
          width: 20,
          height: 20,
          viewBox: '0 0 24 24',
          fill: 'none',
          stroke: 'currentColor',
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        },
        createElement('line', { x1: 22, y1: 2, x2: 11, y2: 13 }),
        createElement('polygon', { points: '22 2 15 22 11 13 2 9 22 2' }),
      ),
    ),
  );
}
